import { faEnvelopeOpen } from '@fortawesome/pro-duotone-svg-icons';
import moment from 'moment';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { UserType } from 'api/models';
import {
  StatusUpdate,
  StatusUpdateState,
  StatusUpdateStatuses,
} from 'api/status-updates';
import { Avatar } from 'components/atoms/avatar';
import {
  EmptyState,
  EmptyStateContainer,
  EmptyStateIcon,
} from 'components/atoms/empty-state';
import { humanise } from 'components/atoms/humanised-date';
import { Tag } from 'components/atoms/tag';
import { getStatusData } from 'components/organisms/projects/project-list/constants';
import { useUser } from 'hooks/use-user';
import styles from './list.module.css';
import {
  CenterWrapper,
  DateWrapper,
  DraftWrapper,
  StatusTitle,
  UnreadIcon,
  Spacer,
} from './styles';

interface RendererProp {
  data: StatusUpdate;
}

const AvatarRenderer: FC<RendererProp> = (props) => {
  const { data } = props;
  const user =
    data.state === StatusUpdateState.Published
      ? data.publishedBy || data.createdBy
      : data.createdBy;

  return (
    <>
      {!data.seenByUser ? <UnreadIcon /> : null}
      <Avatar
        src={user.photoUrl}
        name={`${user?.firstName || ''} ${user?.lastName || ''}`.trim()}
      />
    </>
  );
};

const StatusTag: FC<RendererProp> = ({ data }) => {
  const currentStatus = getStatusData(
    StatusUpdateStatuses,
    data?.status?.statusValue,
  );

  return currentStatus &&
    currentStatus?.label?.toLowerCase() !== StatusUpdateStatuses.none.key ? (
    <div className={styles.tag}>
      <Tag color={currentStatus.color}>{currentStatus.label}</Tag>
    </div>
  ) : null;
};
const StatusUpdateTitleRenderer: FC<RendererProp> = ({ data }) => {
  const { user } = useUser();

  return (
    <CenterWrapper>
      <StatusTitle
        data-cy="status_update_draft_title"
        isUnread={data.seenByUser}
      >
        {data.statusUpdateTitle}
      </StatusTitle>
      {/* check if it is published ,then check if it is auto published if it is autopublished show it only if it is native user user.userType === UserType.Native  */}
      {data.state === StatusUpdateState.Published ? (
        data.status?.statusId === StatusUpdateStatuses.autoPublished.value ? (
          user.userType === UserType.Native ? (
            <StatusTag data={data} />
          ) : null
        ) : (
          <StatusTag data={data} />
        )
      ) : null}
    </CenterWrapper>
  );
};

const TimeRenderer: FC<RendererProp> = ({ data }) => {
  const timeStamp =
    data.state === StatusUpdateState.Published
      ? data.publishedAt
      : data.updatedAt;

  const isSameDay = moment().isSame(timeStamp, 'day');

  return (
    <DateWrapper>
      {isSameDay
        ? moment(timeStamp).format('HH:mm')
        : humanise(moment(timeStamp))}
    </DateWrapper>
  );
};

const DraftRender: FC<RendererProp> = ({ data }) => {
  return (
    <DraftWrapper>
      {data.state === StatusUpdateState.Draft ? '[Draft]' : ''}
    </DraftWrapper>
  );
};

const NoUpdatesEmptyState: FC<{ isDraftEmpty: boolean }> = ({
  isDraftEmpty,
}) => {
  const emptyTitle = isDraftEmpty ? (
    <FormattedMessage id="status_updates.empty.draft.title" />
  ) : (
    <FormattedMessage id="status_updates.empty.sent.title" />
  );

  const emptyMessage = isDraftEmpty ? (
    <FormattedMessage id="status_updates.empty.draft.message" />
  ) : (
    <>
      <Spacer>
        <FormattedMessage id="status_updates.empty.sent.message" />
      </Spacer>
      <div>
        <FormattedMessage id="status_updates.empty.sent.message.footer" />
      </div>
    </>
  );

  return (
    <EmptyStateContainer>
      <EmptyState
        image={<EmptyStateIcon icon={faEnvelopeOpen} />}
        title={emptyTitle}
        message={emptyMessage}
        secondaryCTA={null}
      />
    </EmptyStateContainer>
  );
};

const getPrivateRowStyles = (params) => {
  if ((params.data as StatusUpdate)?.private) {
    return {
      '--ag-odd-row-background-color': 'var(--private-01)',
      '--ag-background-color': 'var(--private-02)',
      '--ag-row-hover-color': 'var(--private-02)',
    };
  }

  return null;
};

export {
  DraftRender,
  TimeRenderer,
  StatusUpdateTitleRenderer,
  AvatarRenderer,
  NoUpdatesEmptyState,
  getPrivateRowStyles,
};
